import { setupModeColors } from "../../SetupMode/colors";
import type { ReactNode } from "react";
import React from "react";
import { styled } from "@linaria/react";
import cn from "classnames";
import RelativeDate from "@ui/RelativeDate";
import Menu from "@ui/Menu";
import Option from "@ui/Option";
import { color, fonts } from "@ui";
import t from "../../../i18n";
import usePrevious from "@ui/lib/hooks/usePrevious";
import { ArrowRightSLine, Attachment2, MoreFill } from "@ui/IconV2/react";
import Button from "@ui/Button";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { spacings } from "@ui/Theme";
import type { FaultEvent } from "../../../api/useFaultEvents";
import { css } from "@linaria/core";
import { Markdown } from "@ui/Markdown";
import { ToolbarSeparator } from "@ui/Markdown/Toolbar/MarkdownToolbar";
import { ToolbarButton } from "@ui/Markdown/Toolbar/ToolbarButton";
import { MessageEditorV2 } from "@ui/MessageEditorV2";

import EditComment from "../components/EditComment";
import type { CommentType } from "../../../api/useComments";

export const THREAD_SELECTED_CN = "thread-selected";
export const THREAD_CN = "thread";

const NO_ANSWER = "no-answer";
const MULTIPLE_ANSWER = "multiple-answer";

export interface Message {
  id: string;
  type: CommentType;
  analysisId: string | null;
  analysisTitle: ReactNode;
  title: (type: CommentType) => ReactNode;
  subtitle: ReactNode;
  answers: Message[];
  date: string;
  // content: ReactNode;
  rawContent: string;
  allowEdit: boolean;
  isAnswer: boolean;
  isDeleted: boolean;
  faultEvent: FaultEvent | null;
}

interface P extends React.HtmlHTMLAttributes<HTMLDivElement> {
  message: Message;
  onDelete: (id: string) => void;
  onEdit: ({
    id,
    content,
    analysisId,
  }: {
    id: string;
    content: string;
    analysisId: string | null;
    type?: CommentType;
  }) => void | Promise<void>;
  onPublish?: (id: string, content: string) => void | Promise<void>;
  defaultDisplayAnswers?: boolean;
  allowAction: boolean;
  canChangeType?: boolean;
  answerPlaceholder?: string;
  toolBarOptions?: ReactNode;
  optionsDirty?: boolean;
  maxLength?: number;
}

type ActionState = "init" | "answer" | "edit" | "delete";

function MessageCard({
  message: {
    type,
    id,
    title,
    subtitle,
    date,
    allowEdit,
    isAnswer,
    answers,
    analysisId,
    rawContent,
  },
  toolBarOptions,
  optionsDirty = false,
  allowAction,
  canChangeType = false,
  answerPlaceholder,
  onDelete,
  onEdit,
  onPublish,
  defaultDisplayAnswers = false,
  ...props
}: P) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  // const editorRef = React.useRef<HTMLDivElement>(null);
  const [actionState, setActionState] = React.useState<ActionState>("init");
  const [draftType, setDraftType] = React.useState(type);
  const displayedType = actionState === "edit" ? draftType : type;
  const [displayAnswers, setDisplayAnswers] = React.useState(defaultDisplayAnswers);
  const isThread = !isAnswer && answers.length > 0;

  React.useEffect(() => {
    if (defaultDisplayAnswers) {
      setDisplayAnswers(defaultDisplayAnswers);
    }
  }, [defaultDisplayAnswers]);

  const setAnswerMode = React.useCallback(() => {
    setActionState("answer");
  }, [setActionState]);

  const setEditMode = React.useCallback(() => {
    setDraftType(type);
    setActionState("edit");
  }, [type, setActionState]);

  const handleDeleteClick = React.useCallback(() => {
    if (isAnswer) {
      onDelete(id);
    } else {
      setActionState("delete");
    }
  }, [isAnswer, onDelete, id]);

  const cancel = React.useCallback(() => {
    setActionState("init");
  }, [setActionState]);

  const confirmDelete = React.useCallback(() => {
    onDelete(id);
  }, [id, onDelete]);

  const publish = React.useCallback(
    async (message: string) => {
      if (onPublish === undefined) return;
      await onPublish?.(id, message);
      setDisplayAnswers(true);
      setActionState("init");
    },
    [id, onPublish, setDisplayAnswers],
  );

  const edit = React.useCallback(
    async (content: string, type?: CommentType) => {
      const res = onEdit({ id, analysisId, content, type });

      if (res instanceof Promise) {
        await res;
      }

      setActionState("init");
    },
    [analysisId, id, onEdit],
  );

  const handleAnswerBlur = React.useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (
        (e.relatedTarget && e.relatedTarget.classList.contains("ReactModal__Content")) ||
        e.target.classList.contains("ReactModal__Content--after-open") ||
        e.target.nodeName === "INPUT"
      ) {
        return;
      }
      setActionState("init");
    },
    [setActionState],
  );

  const toggleDisplay = React.useCallback(() => {
    setDisplayAnswers(!displayAnswers);
  }, [displayAnswers, setDisplayAnswers]);

  const previousActionState = usePrevious(actionState);
  const previousDisplayAnswers = usePrevious(displayAnswers);

  React.useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) throw new Error("Missing wrapper ref");
    if (
      (displayAnswers && !previousDisplayAnswers) ||
      (actionState === "answer" && previousActionState !== "answer") ||
      (actionState === "edit" && previousActionState !== "edit")
    )
      window.requestAnimationFrame(() => wrapper.scrollIntoView({ block: "nearest" }));
  }, [actionState, displayAnswers, previousActionState, previousDisplayAnswers]);

  return (
    <Wrapper
      ref={wrapperRef}
      className={cn({
        answer: isAnswer,
        [setupMessage]: displayedType === "SETUP",
        delete: actionState === "delete",
      })}
      onMouseEnter={e => {
        props.onMouseEnter?.(e);
      }}
      onMouseLeave={e => {
        props.onMouseLeave?.(e);
      }}
      onClick={e => {
        props.onClick?.(e);
      }}
    >
      <div className={header}>
        {title(displayedType)}
        <TimeElapsed date={new Date(date).getTime()} />
        {actionState === "init" && (allowAction || allowEdit) && (
          <Menu
            gap={0}
            arrowWidth={0}
            overlay={
              <>
                {allowEdit && (
                  <Option onClick={setEditMode}>{t("commons.message_card.edit")}</Option>
                )}
                {allowAction && (
                  <Option onClick={handleDeleteClick}>
                    {isThread
                      ? t("commons.message_card.delete_thread")
                      : t("commons.message_card.delete")}
                  </Option>
                )}
              </>
            }
          >
            <HeaderActions>
              <ButtonIconV2>
                <MoreFill />
              </ButtonIconV2>
            </HeaderActions>
          </Menu>
        )}
      </div>
      {subtitle}
      <Content>
        {actionState === "edit" ? (
          <EditComment
            content={rawContent}
            type={type}
            canChangeType={canChangeType}
            onTypeChange={setDraftType}
            onSave={edit}
            onCancel={cancel}
            maxLength={props.maxLength}
          />
        ) : (
          <Markdown defaultValue={rawContent} />
        )}
      </Content>
      <Actions>
        {!answers.length && !isAnswer && actionState === "init" && allowAction && (
          <AnswerAction onClick={setAnswerMode} className={cn({ [NO_ANSWER]: answers.length < 1 })}>
            {t("commons.message_card.answer")}
          </AnswerAction>
        )}

        {answers.length > 0 && actionState !== "delete" && (
          <AnswerAction onClick={toggleDisplay} className={cn({ [NO_ANSWER]: answers.length < 1 })}>
            <Caret className={cn({ rotated: displayAnswers })}>&#x25B8;</Caret>
            {t("commons.message_card.answers", { count: answers.length })}
          </AnswerAction>
        )}

        {actionState === "delete" && (
          <DeleteWraper>
            <DeleteMessage>
              {isThread
                ? t("commons.message_card.ask_delete_thread")
                : t("commons.message_card.ask_delete")}
            </DeleteMessage>
            <DeleteButtonWraper>
              <Button variant="ghost" onClick={cancel} slim>
                {t("commons.message_card.cancel")}
              </Button>
              <Button variant="destructive" onClick={confirmDelete} slim>
                {t("commons.message_card.confirm_delete")}
              </Button>
            </DeleteButtonWraper>
          </DeleteWraper>
        )}
      </Actions>
      {displayAnswers &&
        answers
          .sort(comparatorByDateAsc)
          .map(answer => (
            <MessageCard
              allowAction={allowAction}
              canChangeType={canChangeType}
              key={answer.id}
              message={answer}
              onDelete={onDelete}
              onEdit={onEdit}
              maxLength={props.maxLength}
            />
          ))}
      {((answers.length < 1 && actionState === "answer") ||
        (answers.length > 0 && displayAnswers)) &&
        allowAction && (
          <MessageEditorV2
            className={cn(answerEditor, { [MULTIPLE_ANSWER]: answers.length > 0 })}
            onCancel={cancel}
            onPublish={publish}
            onBlur={handleAnswerBlur}
            placeholder={answerPlaceholder}
            autoFocus={!answers.length}
            maxLength={props.maxLength}
          >
            {(Toolbar, richText) => (
              <>
                <Toolbar>
                  <ToolbarSeparator />
                  <ToolbarButton
                    description={t("comments.tooltip.feature_under_development")}
                    disabled
                  >
                    <Attachment2 size="s" />
                  </ToolbarButton>
                </Toolbar>
                {richText}
              </>
            )}
          </MessageEditorV2>
        )}
    </Wrapper>
  );
}

export default MessageCard;

//#region Functions
function comparatorByDateAsc(a: Message, b: Message) {
  return a.date.localeCompare(b.date);
}
//#endregion Functions
//#region CSS
const DeleteWraper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 12px;
`;

const DeleteMessage = styled.div`
  margin-left: 10px;
  color: ${color.oxygen400};
  font: ${fonts.body1SemiBold};
`;

const DeleteButtonWraper = styled.div`
  display: flex;
  gap: 8px;
`;

const AnswerAction = styled.div`
  display: flex;
  color: ${color.lightNitrogen500_Main};
  font: ${fonts.body2};
  cursor: pointer;
  margin-right: auto;

  /* ordering maters */
  opacity: 1;
  margin-left: -3px;
  &.${NO_ANSWER} {
    opacity: 0;
    margin-left: 0px;
  }
  .${THREAD_CN}:hover &,
  .${THREAD_SELECTED_CN} & {
    opacity: 1;
  }
`;

const Caret = styled(ArrowRightSLine)`
  position: relative;
  top: 1px;
  height: 18px;
  width: 18px;
  margin-right: 4px;
  &.rotated {
    transform: rotate(90deg);
  }
`;

const HeaderActions = styled.div`
  position: relative;
  cursor: pointer;

  .${THREAD_CN}:not(:hover) & {
    display: none;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 16px;

  &.answer {
    padding-left: 24px;
    padding-right: 0px;
  }

  .delete {
    /* NOTE: Linaria don't like var interpolation inside rule value.
    We need to extract it into selector scoped variable. */
    --box-shadow-color: ${color.oxygen400};
    box-shadow: 2px 0 0 0 var(--box-shadow-color) inset;
    background-color: ${color.oxygen50};
    margin: 20px 0px;
  }

  &.delete {
    /* NOTE: Linaria don't like var interpolation inside rule value.
    We need to extract it into selector scoped variable. */
    --box-shadow-color: ${color.oxygen400};
    box-shadow: 2px 0 0 0 var(--box-shadow-color) inset;
    background-color: ${color.oxygen50};
    .answer {
      background-color: ${color.oxygen50};
    }
    :hover .answer {
      background-color: ${color.oxygen50};
    }
  }
`;

const header = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 24px;
  width: 100%;
  gap: ${spacings.xxs};

  & > :nth-child(1) {
    /* Title */
    color: ${color.carbon900};
    font: ${fonts.body1SemiBold};
  }
`;

const TimeElapsed = styled(RelativeDate)`
  font: ${fonts.body1};
  color: ${color.carbon700};
  white-space: nowrap;
  flex-shrink: 0;

  display: flex;
  .${THREAD_CN}:hover & {
    display: none;
  }
`;

const Content = styled.div`
  margin-top: 8px;
  color: ${color.carbon900};
  font: ${fonts.body1};
`;

const Actions = styled.div`
  margin-top: ${spacings.xs};
  display: flex;
  align-items: center;
`;

const answerEditor = css`
  padding: 12px 0px 12px 0;

  &.${MULTIPLE_ANSWER} {
    padding: 12px 0px 12px 24px;
  }
`;
const setupMessage = css`
  && {
    background-color: ${setupModeColors.background};
    box-shadow: inset 3px 0 ${setupModeColors.primary};
  }
`;
//#endregion CSS
